// Initialize the desktop menu independently of deferred theme modules.
(() => {
class DesktopHeaderCascade extends HTMLElement {
  /** @type {((event: MouseEvent) => void) | null} */
  #outsideClickListener = null;

  /** @type {HTMLElement[]} */
  #sources = [];

  /** @type {boolean} */
  #bound = false;

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {(() => void) | null} */
  #resizeListener = null;

  /** @type {number | null} */
  #resizeRafId = null;

  /** @type {boolean} */
  #bandWidthLocked = false;

  /** @type {ResizeObserver | null} */
  #headerLayoutObserver = null;

  connectedCallback() {
    this.#sources = Array.from(
      this.querySelectorAll('[data-source-id][data-source-opens-drawer="true"]')
    ).filter((source) => source instanceof HTMLElement);

    if (!this.#bound) {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('mouseover', this.#onMouseOver);
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('keydown', this.#onKeyDown);
      this.#bound = true;
    }

    this.#bindHeaderLayoutObserver();
    this.#syncInitialState();
    this.#updateCascadeLayout();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#bound = false;
    this.#setOpenState(false);
    this.#unbindResize();
    this.#unbindHeaderLayoutObserver();
    this.#sources = [];
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSources() {
    return this.#sources;
  }

  #bindOutsideClick() {
    if (this.#outsideClickListener) return;

    this.#outsideClickListener = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (this.contains(target)) return;

      this.#closeAllSources();
    };

    document.addEventListener('click', this.#outsideClickListener, { capture: true });
  }

  #unbindOutsideClick() {
    if (!this.#outsideClickListener) return;

    document.removeEventListener('click', this.#outsideClickListener, { capture: true });
    this.#outsideClickListener = null;
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  #updateBandWidth(options = {}) {
    if (this.#bandWidthLocked && !options.force) return;

    const row = this.querySelector('.desktop-header-cascade__row');
    if (!(row instanceof HTMLElement)) return;

    const bandWidth = Math.ceil(row.getBoundingClientRect().right);
    this.style.setProperty('--sk-desktop-cascade-band-width', `${bandWidth}px`);
    this.#bandWidthLocked = true;
  }

  #clearBandWidth() {
    this.style.removeProperty('--sk-desktop-cascade-band-width');
    this.#bandWidthLocked = false;
  }

  #bindResize() {
    if (this.#resizeListener) return;

    this.#resizeListener = () => {
      if (this.#resizeRafId !== null) return;

      this.#resizeRafId = requestAnimationFrame(() => {
        this.#resizeRafId = null;
        if (this.getAttribute('data-cascade-open') === 'true') {
          this.#updateBandWidth({ force: true });
        }
        this.#updateCascadeLayout();
      });
    };

    window.addEventListener('resize', this.#resizeListener);
  }

  #bindHeaderLayoutObserver() {
    if (this.#headerLayoutObserver) return;

    const headerGroup = document.getElementById('header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerLayoutObserver = new ResizeObserver(() => {
      this.#updateCascadeLayout();
    });
    this.#headerLayoutObserver.observe(headerGroup);
  }

  #unbindHeaderLayoutObserver() {
    this.#headerLayoutObserver?.disconnect();
    this.#headerLayoutObserver = null;
  }

  /**
   * @param {string} variable
   * @param {number} fallback
   * @returns {number}
   */
  #readCssLengthPx(variable, fallback) {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;width:var(${variable});`;
    this.append(probe);
    const px = probe.getBoundingClientRect().width;
    probe.remove();
    return px || fallback;
  }

  /**
   * Keeps drawer level-one links below header tabs with aligned horizontal inset.
   */
  #updateCascadeLayout() {
    const row = this.querySelector('.desktop-header-cascade__row');
    if (!(row instanceof HTMLElement)) return;

    const triggerLabels = [...row.querySelectorAll('.desktop-header-cascade__trigger-label')].filter(
      (label) => label.getBoundingClientRect().height > 0 && label.getBoundingClientRect().top > 0
    );
    const triggerLabel = triggerLabels[0] ?? row.querySelector('.desktop-header-cascade__trigger-label');
    if (!(triggerLabel instanceof HTMLElement)) return;

    const labelRect = triggerLabel.getBoundingClientRect();
    const menuGap = this.#readCssLengthPx('--desktop-header-cascade-menu-gap', 16);
    const menuTop = Math.round(labelRect.bottom + menuGap);
    const labelStyle = getComputedStyle(triggerLabel);
    const labelPaddingStart =
      Number.parseFloat(labelStyle.paddingInlineStart) || Number.parseFloat(labelStyle.paddingLeft) || 0;
    const itemInset = Math.round(labelRect.left + labelPaddingStart);

    this.style.setProperty('--desktop-header-cascade-menu-top', `${menuTop}px`);
    this.style.setProperty('--desktop-header-cascade-item-inset', `${itemInset}px`);
  }

  #unbindResize() {
    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
      this.#resizeListener = null;
    }

    if (this.#resizeRafId !== null) {
      cancelAnimationFrame(this.#resizeRafId);
      this.#resizeRafId = null;
    }
  }

  #syncInitialState() {
    const activeSource = this.#getSources().find(
      (source) => source.getAttribute('data-source-active') === 'true'
    );

    if (activeSource) {
      this.#activateSource(activeSource, { preserveLevelOne: true });
    } else {
      this.#closeAllSources();
    }
  }

  /**
   * @param {Element} element
   * @returns {HTMLElement | null}
   */
  #getContainingSource(element) {
    const source = element.closest('[data-source-id]');
    return source instanceof HTMLElement && this.#getSources().includes(source) ? source : null;
  }

  /**
   * @param {HTMLElement} source
   * @param {{ preserveLevelOne?: boolean }} [options]
   */
  #activateSource(source, options = {}) {
    if (source.getAttribute('data-source-opens-drawer') !== 'true') return;

    this.#getSources().forEach((item) => {
      const isActive = item === source;
      item.setAttribute('data-source-active', isActive ? 'true' : 'false');

      const trigger = item.querySelector('[data-source-trigger]');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      const panel = item.querySelector('[data-source-panel]');
      if (panel instanceof HTMLElement) {
        panel.hidden = !isActive;
      }
    });

    this.#setOpenState(true);

    if (!options.preserveLevelOne) {
      this.#resetLevelOne(source);
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {string} levelOneId
   */
  #activateLevelOne(source, levelOneId) {
    if (source.getAttribute('data-source-has-level-two') !== 'true') return;
    if (source.getAttribute('data-active-level-one') === levelOneId) return;

    const panel = source.querySelector(`[data-level-one-panel="${levelOneId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    source.setAttribute('data-level-one-active', 'true');
    source.setAttribute('data-active-level-one', levelOneId);

    source.querySelectorAll('[data-level-one-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      const isActive = trigger.getAttribute('data-level-one-trigger') === levelOneId;
      trigger.classList.toggle('desktop-header-cascade__level-one-link--active', isActive);
    });

    source.querySelectorAll('[data-level-one-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = panel.getAttribute('data-level-one-panel') !== levelOneId;
    });
  }

  /**
   * @param {HTMLElement} source
   */
  #resetLevelOne(source) {
    source.setAttribute('data-level-one-active', 'false');
    source.removeAttribute('data-active-level-one');

    source.querySelectorAll('[data-level-one-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.classList.remove('desktop-header-cascade__level-one-link--active');
    });

    source.querySelectorAll('[data-level-one-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = true;
    });
  }

  #closeAllSources() {
    this.#getSources().forEach((source) => {
      source.setAttribute('data-source-active', 'false');
      this.#resetLevelOne(source);

      const trigger = source.querySelector('[data-source-trigger]');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }

      const panel = source.querySelector('[data-source-panel]');
      if (panel instanceof HTMLElement) {
        panel.hidden = true;
      }
    });
    this.#setOpenState(false);
  }

  /**
   * @param {boolean} isOpen
   */
  #setOpenState(isOpen) {
    const backdrop = this.querySelector('[data-cascade-backdrop]');
    if (backdrop instanceof HTMLElement) {
      backdrop.hidden = !isOpen;
    }

    const footer = this.querySelector('[data-cascade-footer]');
    if (footer instanceof HTMLElement) {
      footer.hidden = !isOpen;
    }

    if (!isOpen) {
      this.querySelectorAll('.sk-drawer-footer__localization-disclosure[open]').forEach((details) => {
        if (details instanceof HTMLDetailsElement) {
          details.open = false;
        }
      });
    }

    if (isOpen) {
      document.documentElement.setAttribute('scroll-lock', '');
      this.#ownsScrollLock = true;
      if (!this.#bandWidthLocked) {
        this.#updateBandWidth({ force: true });
      }
      this.setAttribute('data-cascade-open', 'true');
      this.#bindResize();
      this.#bindOutsideClick();
      requestAnimationFrame(() => this.#updateCascadeLayout());
      return;
    }

    this.setAttribute('data-cascade-open', 'false');
    requestAnimationFrame(() => this.#updateCascadeLayout());
    this.#unbindResize();
    this.#clearBandWidth();
    this.#unbindOutsideClick();

    if (this.#ownsScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#ownsScrollLock = false;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-source-direct-link]')) return;

    if (target.closest('[data-cascade-backdrop]')) {
      event.preventDefault();
      this.#closeAllSources();
      return;
    }

    const levelOneTrigger = target.closest('[data-level-one-trigger]');
    if (levelOneTrigger instanceof HTMLElement) {
      event.preventDefault();
      this.#activateLevelOneFromTrigger(levelOneTrigger);
      return;
    }

    const trigger = target.closest('[data-source-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const source = this.#getContainingSource(trigger);
    if (!source) return;

    event.preventDefault();

    const isActive = source.getAttribute('data-source-active') === 'true';
    if (isActive) {
      this.#closeAllSources();
      return;
    }

    this.#activateSource(source);
  };

  /**
   * @param {MouseEvent} event
   */
  #onMouseOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-level-one-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.#activateLevelOneFromTrigger(trigger);
  };

  /**
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-level-one-trigger]');
    if (!(trigger instanceof HTMLElement)) return;

    this.#activateLevelOneFromTrigger(trigger);
  };

  /**
   * @param {HTMLElement} trigger
   */
  #activateLevelOneFromTrigger(trigger) {
    if (!trigger.hasAttribute('data-level-one-trigger')) return;

    const source = this.#getContainingSource(trigger);
    const levelOneId = trigger.getAttribute('data-level-one-trigger');

    if (!source || !levelOneId) return;
    if (source.getAttribute('data-source-has-level-two') !== 'true') return;

    this.#activateLevelOne(source, levelOneId);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.#closeAllSources();
  };
}

if (!customElements.get('desktop-header-cascade')) {
  customElements.define('desktop-header-cascade', DesktopHeaderCascade);
}

})();
